<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getJobExperienceOptions } from "@/helpers/filterOptions";
import { resolveJobDisplayName } from "@/helpers/workerJob";
import { normalizeJobExperience } from "@/helpers/workerJobExperience";

import type { ProfilPreviewQualificationsProps, QualificationsTableRow } from "@/components/modal/Profile/previewProfile/types";
import type { TableAltColumns } from "@/dsComponents/TableAlt/types";
import type { LabelValue } from '@/types/common';
import type { IInterimJob, JobOption } from '@/types/job';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import AsideSectionTitle from "@/dsComponents/modals/subcomponents/AsideSectionTitle.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import ExperiencesDropdown from "@/components/dropdown/ExperiencesDropdown.vue";
import JobsDropdown from "@/components/dropdown/JobsDropdown.vue";

const props = defineProps<ProfilPreviewQualificationsProps>();

const { t } = useI18n();

const emit = defineEmits<{
    (e: "update:experience", jobIndex: number, newExperienceValue: LabelValue | null): void;
    (e: "addJob", job: JobOption): void;
    (e: "removeJob", jobIndex: number): void;
}>();

const jobOffersDisplayed = ref(true);
const addJobSelection = ref<JobOption | null>(null);

const profileJobIds = computed(() => (props.profile?.jobs ?? []).map((job) => job.jobId).filter(Boolean));

const formatExperience = (experience: string | null | undefined): LabelValue | null => {
    const normalized = normalizeJobExperience(experience);
    if (!normalized) return null;
    return getJobExperienceOptions().find((opt) => opt.value === normalized) ?? null;
};

const getJobLabel = (job: IInterimJob): string => resolveJobDisplayName(job);

const handleAddJobSelect = (job: JobOption | null) => {
    if (!job) return;
    emit("addJob", job);
    addJobSelection.value = null;
};

const qualificationsColumns: TableAltColumns[] = [{ field: "job" }, { field: "experience" }, { field: "actions" }];

const qualificationsRows = computed<QualificationsTableRow[]>(() =>
    (props.profile?.jobs ?? []).map((job) => ({
        content: {},
        editMode: true,
        job,
    }))
);
</script>

<template>
    <div class="profil-preview-qualifications">
        <AsideSectionTitle
            :title="t('modals.profilPreview.sections.qualifications.title')"
            size="md"
            default-open
            data-bot="toggle-jobs"
            @toggle-section="jobOffersDisplayed = $event"
        >
            <template #actions>
                <JobsDropdown
                    :modelValue="addJobSelection"
                    :exclude-job-ids="profileJobIds"
                    :fitContent="false"
                    trigger-icon-only="plus"
                    size="md"
                    data-bot="add-job"
                    @update:modelValue="handleAddJobSelect"
                />
            </template>
        </AsideSectionTitle>
        <TableAlt v-if="jobOffersDisplayed" :columns="qualificationsColumns" :rows="qualificationsRows">
            <template #edit="{ row, rowIndex }">
                <div class="profil-preview-qualifications__edit-row">
                    <div class="profil-preview-qualifications__edit-row__job">
                        <span class="profil-preview-qualifications__edit-row__job-label typo-xxs-medium">
                            {{ getJobLabel((row as QualificationsTableRow).job) }}
                        </span>
                    </div>
                    <div class="profil-preview-qualifications__edit-row__experience">
                        <ExperiencesDropdown
                            :modelValue="formatExperience((row as QualificationsTableRow).job.experience)"
                            :showIcon="false"
                            :fitContent="false"
                            :placeholder="t('modals.profilPreview.form.placeholder.experience')"
                            data-bot="experience"
                            @update:modelValue="emit('update:experience', rowIndex, $event)"
                        />
                    </div>
                    <div class="profil-preview-qualifications__edit-row__actions">
                        <ButtonAlt iconOnly="trash" type="error" size="sm" data-bot="remove-job" @click="emit('removeJob', rowIndex)" />
                    </div>
                </div>
            </template>
        </TableAlt>
    </div>
</template>

<style scoped lang="scss">
.profil-preview-qualifications {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);
    align-self: stretch;

    &__edit-row {
        display: grid;
        grid-template-columns: 1fr 120px 36px;
        align-items: center;
        gap: var(--spacing-md, 8px);
        width: 100%;
        min-height: 36px;
        padding-left: var(--spacing-md, 8px);
        border-radius: var(--radius-xs, 4px);

        &:hover {
            background-color: var(--bg-lightest-50, #f6f7f9);
        }

        &__job {
            padding: 0 var(--spacing-xs, 4px);
            min-width: 0;

            &-label {
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: var(--text-main-900, #00061a);
            }
        }

        &__experience {
            flex-shrink: 0;
            padding: 0 var(--spacing-xs, 4px);
        }

        &__actions {
            display: flex;
            justify-content: center;
            flex-shrink: 0;
        }
    }
}
</style>
